import type { IconType } from '@/ui/Icon/icons';
import { Icons } from '@/ui/Icon/icons';

export type CustomizeControlId =
  'style' | 'views' | 'sources' | 'messages' | 'schedule';

// The add-content ("Magic") flow rides on the same `edit` param as the customize
// controls, but it isn't one of the customize tabs — it swaps in its own panel
// instead of the customize sidebar/bar. Keeping it a distinct value (not a
// CustomizeControlId) means `activeControlId` stays null and customize mode stays
// inactive while it's open.
export const MAGIC_EDIT_VALUE = 'magic' as const;

// Customize mode's home screen: the card list of every panel, which the bar's
// "All" returns to. Not a panel itself, so activeControlId stays null on it.
export const CUSTOMIZE_HOME_EDIT_VALUE = 'all' as const;

/** Every value the `edit` param can hold: a customize tab, its home, or Magic. */
export type EditValue =
  | CustomizeControlId
  | typeof CUSTOMIZE_HOME_EDIT_VALUE
  | typeof MAGIC_EDIT_VALUE;

// The Magic panel's workflow sub-state rides the panel-scoped `tab` param
// (`edit=magic&tab=<workflow>`); an absent tab means the calendar chat.
export const MAGIC_WORKFLOW_TAB_VALUES = ['add', 'edits'] as const;

export type MagicWorkflow = (typeof MAGIC_WORKFLOW_TAB_VALUES)[number];

export const isMagicWorkflow = (
  value: string | null | undefined
): value is MagicWorkflow =>
  value != null && MAGIC_WORKFLOW_TAB_VALUES.includes(value as MagicWorkflow);

// The Schedule settings panel's sections ride the same panel-scoped `tab` param
// (`edit=schedule&tab=<section>`). Named here rather than left inline in the
// panel because the planner's filter popover deep-links straight to it.
export const SCHEDULE_CATEGORIES_TAB = 'categories';
export const SCHEDULE_SEND_TO_CALENDAR_TAB = 'calendar';

type CustomizeOption = {
  id: CustomizeControlId;
  icon: IconType;
  label: string;
  description: string;
  /** How the panel renders: 'rail' = AH_RAIL_WIDTH sticky sidebar; 'fullscreen' = fixed overlay. */
  presentation: 'rail' | 'fullscreen';
  /** Shown as a tab but not as a card on the customize home screen. */
  hideFromHome?: boolean;
};

export const CUSTOMIZE_OPTIONS: CustomizeOption[] = [
  {
    id: 'style',
    icon: Icons.Palette,
    label: 'Style',
    description: 'Change the vibe, colors, and the look of this schedule',
    presentation: 'rail',
  },
  {
    id: 'views',
    icon: Icons.Layout,
    label: 'Views',
    description: 'Pick what displays on each layout, add or remove views',
    presentation: 'rail',
  },
  {
    id: 'sources',
    icon: Icons.Rss,
    label: 'Sources',
    description:
      'Add other Agenda Hero schedules, your Google Calendar, or ics feeds',
    presentation: 'fullscreen',
  },
  {
    id: 'messages',
    icon: Icons.TextMessage,
    label: 'Text messages',
    description: 'Scheduled text reminders for events on this planner',
    presentation: 'fullscreen',
    hideFromHome: true,
  },
  {
    id: 'schedule',
    icon: Icons.Settings,
    label: 'Schedule settings',
    description:
      'Time zone, categories, Send to calendar, and schedule duration',
    presentation: 'fullscreen',
  },
];

// The `edit` URL param is keyed directly off these control ids — used to validate
// the param value and to gate customize mode (a valid id ⟺ customize mode active).
export const isCustomizeControlId = (
  value: string | null | undefined
): value is CustomizeControlId =>
  value != null && CUSTOMIZE_OPTIONS.some((option) => option.id === value);
